import styled from '@emotion/styled';
import { SERVICES } from 'constant/appConstant';
import { breakPoints } from 'constant/breakPoints';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import BannerServicesItem from './Item';
import gsap from 'gsap';
import { RectangleBlur } from 'styles/styled';

let timeout: any;

export default function BannerServices() {
  const refWrapItem = useRef<HTMLDivElement>(null);
  const refSliderControl = useRef<HTMLDivElement>(null);
  const refTop = useRef<HTMLDivElement>(null);
  const refScrollTop = useRef<HTMLDivElement>(null);
  const isFirstTime = useRef(true);

  const [step, setStep] = useState(0);
  const [rtl, setRTL] = useState(false);

  const itemRender = useMemo(() => {
    const start = step;
    let end = step + (rtl ? 1 : -1);

    end = end === SERVICES.length ? 0 : end < 0 ? SERVICES.length - 1 : end;

    return rtl
      ? [SERVICES[start], SERVICES[end]]
      : [SERVICES[end], SERVICES[start]];
  }, [rtl, step]);

  // Calc width of SliderControl
  useEffect(() => {
    if (!refTop.current || !refSliderControl.current || !refScrollTop.current)
      return;

    const eleActive: any = refTop.current.querySelector('.active');

    if (!eleActive) return;

    refSliderControl.current.style.width =
      (eleActive.clientWidth || 255) + 'px';

    gsap.to(refSliderControl.current, {
      left: eleActive.offsetLeft,
      duration: 0.05,
      ease: 'linear',
    });

    gsap.to(refScrollTop.current, {
      scrollTop: 0,
      scrollLeft: eleActive.offsetLeft,
      duration: 0.5,
      ease: 'ease',
    });
  }, [step]);

  // Auto slide item
  useEffect(() => {
    clearTimeout(timeout);

    timeout = setTimeout(
      () => {
        isFirstTime.current = false;

        const newStep = step === SERVICES.length - 1 ? 0 : step + 1;

        setStep(newStep);
      },
      isFirstTime.current ? 6_000 : 3_000,
    );

    return () => {
      clearTimeout(timeout);
    };
  }, [step]);

  return (
    <Wrap>
      <RectangleBlur
        width="397px"
        rotate={45.07}
        right="-245px"
        bottom="86px"
      />
      <RectangleBlur width="450px" rotate={-135} left="-265px" bottom="-85px" />

      <WrapTop ref={refScrollTop}>
        <RectangleBlur
          width="450px"
          rotate={-135}
          left="-265px"
          bottom="-85px"
        />
        <Top ref={refTop}>
          <SliderControl ref={refSliderControl} />
          {SERVICES.map((item, idx) => (
            <TopItem
              className={step === idx ? 'active' : ''}
              key={idx}
              onClick={() => {
                setStep(idx);
                setRTL(idx < step);
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: item.title }} />
            </TopItem>
          ))}
        </Top>
      </WrapTop>
      <WrapItem>
        <List ref={refWrapItem} className={rtl ? 'rtl' : 'ltr'}>
          {itemRender.map((item) => (
            <div key={item.id}>
              <BannerServicesItem
                title={item.title}
                description={item.description}
                src={item.img}
                id={item.id}
              />
            </div>
          ))}
        </List>
      </WrapItem>
    </Wrap>
  );
}

const Wrap = styled.div`
  position: relative;
  padding-top: 90px;
  min-height: calc(100vh - 200px);
  z-index: 10;

  @media screen and (max-width: ${breakPoints.tabletS}px) {
    & > div:nth-of-type(1) {
      right: -355px;
      bottom: -90px;
    }

    & > div:nth-of-type(2) {
      left: -345px;
      bottom: -270px;
    }
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    display: flex;
    height: 100vh;
    flex-direction: column-reverse;
    justify-content: space-between;

    & > div:nth-of-type(1),
    & > div:nth-of-type(2) {
      z-index: 10;
    }

    & > div:nth-of-type(2) {
      width: 400px;
    }
  }

  @media screen and (min-width: ${breakPoints.desktopM}px) {
    & > div:nth-of-type(1) {
      z-index: 10;
    }

    & > div:nth-of-type(2) {
      width: 450px;
    }
  }
`;

const WrapTop = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  width: calc(100vw + 16px);
  z-index: 20;
  position: relative;

  ::-webkit-scrollbar {
    height: 0;
  }

  &:before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, #3c235c 0%, rgba(60, 35, 92, 0) 100%);
    display: none;
  }

  &:after {
    content: '';
    position: absolute;
    z-index: -2;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: var(--purple-darken);
  }

  & > div:nth-of-type(1) {
    display: none;
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    height: 92px;

    &:before {
      display: initial;
    }

    & > div:nth-of-type(1) {
      display: initial;
      width: 400px;
    }
  }

  @media screen and (min-width: ${breakPoints.desktopM}px) {
    & > div:nth-of-type(1) {
      width: 450px;
    }
  }
`;
const Top = styled.div`
  display: flex;
  position: relative;
  width: max-content;
  flex-wrap: nowrap;
  justify-content: space-between;
  max-width: 1150px;
  margin: 0 auto;
  z-index: 200;

  &:before {
    content: '';
    position: absolute;
    z-index: -1;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(180deg, #3c235c 0%, rgba(60, 35, 92, 0) 100%);
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    background: transparent;

    &:before {
      display: none;
    }
  }
`;
const TopItem = styled.div`
  padding: 16px 20px;
  font-weight: 500;
  font-size: 16px;
  position: relative;
  color: #997eba;
  width: max-content;
  cursor: pointer;

  &:hover {
    color: #fff;
  }

  &.active {
    color: #fff;
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    padding: 21px 0;
    width: 20vw;
    max-width: 228px;
    display: flex;
    justify-content: center;
  }
`;
const SliderControl = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  height: 5px;
  transition: all 0.5s ease;
  width: 228px;
  background: linear-gradient(
    302.05deg,
    #a336d9 16.75%,
    rgba(163, 54, 217, 0.6) 73.45%
  );
`;

const WrapItem = styled.div`
  background-image: url('/images/blur.webp');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  z-index: 20;
  position: relative;
  height: calc(100vh - 182px);

  ::-webkit-scrollbar {
    height: 0;
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    flex: 1;
    background-position: -300px -100px;
    background-size: 100% 100%;
  }
`;
const List = styled.div`
  display: flex;
  flex-wrap: nowrap;

  & > div {
    width: 100vw;
    flex: 0 0 100vw;
    opacity: 0;
    will-change: transform;
  }

  &.ltr {
    & > div:first-of-type {
      animation: ltrLeft 0.6s ease-out forwards;
    }

    & > div:last-of-type {
      animation: ltrMain 0.6s ease-out forwards;
    }

    @keyframes ltrLeft {
      0% {
        opacity: 1;
        /* left: 50vw; */
        transform: translateX(50%);
      }

      100% {
        opacity: 0;
        /* left: -80vw; */
        transform: translateX(-80%);
      }
    }
    @keyframes ltrMain {
      0% {
        opacity: 0.2;
        /* left: 0; */
        transform: translateX(0);
      }

      100% {
        opacity: 1;
        /* left: -100vw; */
        transform: translateX(-100%);
      }
    }
  }

  &.rtl {
    margin-left: -100vw;

    & > div:first-of-type {
      animation: rtlMain 0.6s ease-out forwards;
    }

    & > div:last-of-type {
      animation: rtlRight 0.6s ease-out forwards;
    }

    @keyframes rtlRight {
      0% {
        opacity: 0.5;
        transform: translateX(-50%);
      }

      100% {
        opacity: 0;
        transform: translateX(80%);
      }
    }
    @keyframes rtlMain {
      0% {
        opacity: 0;
        transform: translateX(0);
      }

      100% {
        opacity: 1;
        transform: translateX(100%);
      }
    }
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    height: 100%;
  }
`;
